import { BreadcrumbsItem } from '@wix/design-system';
import debounce from 'lodash/debounce';
import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { useStore } from 'react-redux';

import { logBreadcrumbsClickAction, logBreadcrumbsScroll } from '../../../bi';
import { TRootState } from '../../../common/reducers';
import { HOME_CATEGORY_ID } from './Breadcrumbs';

const DEBOUNCE_TIME_MS = 150;
const HUNDRED_PERCENT = 100;

export const logScrolledBreadcrumbs = debounce(
  (
    state: TRootState,
    event,
    shouldSkipScrollEvent: MutableRefObject<boolean>,
    lastScrollLeftPosition: MutableRefObject<number | undefined>,
  ) => {
    const scrollAreaWidth = event.target.scrollWidth - event.target.clientWidth;

    if (shouldSkipScrollEvent.current || scrollAreaWidth === 0) {
      shouldSkipScrollEvent.current = false;
      return;
    }

    const percentScrolled = Math.round((event.target.scrollLeft / scrollAreaWidth) * HUNDRED_PERCENT);
    const scrollDirection = event.target.scrollLeft < lastScrollLeftPosition.current! ? 'left' : 'right';

    lastScrollLeftPosition.current = event.target.scrollLeft;

    logBreadcrumbsScroll(state, {
      percentScrolled,
      scrollDirection,
    });
  },
  DEBOUNCE_TIME_MS,
);

type UseBreadcrumbsBIParams = {
  breadcrumbsContainer: MutableRefObject<HTMLDivElement | null>;
  breadcrumbs: BreadcrumbsItem[];
  shouldDisplayBreadcrumbs: boolean;
};

export const useBreadcrumbsBI = ({
  breadcrumbsContainer,
  breadcrumbs,
  shouldDisplayBreadcrumbs,
}: UseBreadcrumbsBIParams) => {
  const store = useStore();
  const lastScrollLeftPosition = useRef<number>();
  const shouldSkipScrollEvent = useRef(true); // skip initial scroll event which is triggered by page load

  useEffect(() => {
    if (breadcrumbsContainer.current && shouldDisplayBreadcrumbs) {
      const scrollAreaWidth =
        breadcrumbsContainer.current.scrollWidth - breadcrumbsContainer.current.clientWidth;

      const willScroll = breadcrumbsContainer.current.scrollLeft < scrollAreaWidth;
      if (!willScroll) {
        return;
      }

      // Do not log BI events caused by manual scroll
      shouldSkipScrollEvent.current = true;
      breadcrumbsContainer.current.scrollLeft = scrollAreaWidth;
      lastScrollLeftPosition.current = breadcrumbsContainer.current.scrollLeft;
    }
  }, [breadcrumbs, breadcrumbsContainer, shouldDisplayBreadcrumbs]);

  const scrollEventHandler = useCallback(
    (event) => logScrolledBreadcrumbs(store.getState(), event, shouldSkipScrollEvent, lastScrollLeftPosition),
    [store, shouldSkipScrollEvent, lastScrollLeftPosition],
  );

  const handleBreadcrumbClickBI = useCallback(
    (breadcrumb: BreadcrumbsItem) => {
      const state = store.getState();
      const breadcrumbClicked = breadcrumbs.find((b) => b.id === breadcrumb.id)!;
      const breadcrumbIndex = breadcrumbs.findIndex((b) => b.id === breadcrumb.id);
      const isHomeCategory = breadcrumbClicked.id === HOME_CATEGORY_ID;

      logBreadcrumbsClickAction(state, {
        isHomeCategory,
        id: breadcrumbClicked.id.toString(),
        index: breadcrumbIndex,
        name: breadcrumbClicked.value?.toString(),
      });
    },
    [store, breadcrumbs],
  );

  return { handleBreadcrumbClickBI, scrollEventHandler };
};
